/* Work schedule editor (overlay). A week preview, then the windows: day toggles,
   from–to, the AI regime (+ Call chatter when that regime hands off), and a per-window Advanced with ignore lists,
   interrupts and sales & requests. Every edit goes straight to the page draft,
   which autosaves — "Done" only closes. Esc closes an open menu first. */

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import type { WorkWindow } from '../../../../api/client';
import { AI_BASE_REGIME_OPTIONS, regimeCompletionStep } from '../../aiRegimeOptions';
import { FOLDER_INTERRUPT_OVERRIDE_FIELDS } from '../../folderInterruptOverrides';
import { AdvancedToggle, Btn, Dropdown, Icon, Seg } from '../kit';
import { font as f } from '../style';
import { C } from '../tokens';
import { flattenWindows, groupWindows, hhmm, newWindow, parseHhmm, weekSegments, weeklyHours, windowChanges, type EdWindow } from './model';
import { AddLists, Chip, ChipRow } from './parts';

const TIMEZONES = ['Europe/Kyiv', 'Europe/London', 'Europe/Berlin', 'Europe/Moscow', 'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles', 'America/Sao_Paulo', 'Asia/Dubai', 'Asia/Kolkata', 'Asia/Singapore', 'Asia/Tokyo', 'Australia/Sydney', 'UTC'];
const DEF = '__default__';

export function ScheduleModal({ tz, windows, regime, regimeId, chatter, lists, onTz, onWindows, onClose }: {
  tz: string;
  windows: WorkWindow[];
  /** The account's default regime label, shown on "Default". */
  regime: string;
  /** The account's default regime id and Call chatter, inherited by windows that don't set them. */
  regimeId: string;
  chatter: boolean;
  lists: string[];
  onTz: (tz: string) => void;
  onWindows: (w: WorkWindow[]) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation('assistantCompact');
  const { t: tc } = useTranslation('chatAssistant');
  // Editor windows live here while the modal is open, so a window with no days
  // ticked isn't dropped mid-edit (it simply isn't saved).
  const [ws, setWs] = useState<EdWindow[]>(() => groupWindows(windows));
  const [adv, setAdv] = useState<Record<string, boolean>>({});
  const commit = (next: EdWindow[]) => { setWs(next); onWindows(flattenWindows(next)); };
  const patch = (k: string, p: Partial<EdWindow>) => commit(ws.map((w) => (w.key === k ? { ...w, ...p } : w)));

  useEffect(() => {
    const k = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [onClose]);

  const flat = flattenWindows(ws);
  const segs = weekSegments(flat);
  const hrs = Math.round(weeklyHours(flat));
  const days = [0, 1, 2, 3, 4, 5, 6];
  const tzOpts = (TIMEZONES.includes(tz) ? TIMEZONES : [tz, ...TIMEZONES]).map((z) => ({ id: z, label: z.replace(/_/g, ' ') }));
  const regOpts = [{ id: DEF, label: t('su.defaultIs', { v: regime }), sub: t('su.followsAccount') }, ...AI_BASE_REGIME_OPTIONS.map((o) => ({ id: o.value, label: o.label, sub: o.description }))];
  const tri = [{ id: 'd', label: t('su.default') }, { id: 'on', label: t('su.on') }, { id: 'off', label: t('su.off') }];
  const modes = (decline: boolean) => [{ id: 'd', label: t('su.default') }, { id: 'handoff', label: t('su.handoff') }, { id: 'automate', label: t('su.automate') }, ...(decline ? [{ id: 'decline', label: t('su.decline') }] : [])];

  return createPortal(
    <div className="cpt-root" style={{ position: 'fixed', inset: 0, zIndex: 1200, background: 'color-mix(in srgb, var(--om-page) 72%, transparent)', display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '40px 24px', boxSizing: 'border-box' }}>
      <div role="dialog" aria-modal="true" style={{ width: 860, maxWidth: '100%', maxHeight: '100%', background: C.card, border: `1px solid ${C.borderHi}`, borderRadius: 10, boxShadow: '0 16px 48px rgba(0,0,0,.55)', display: 'flex', flexDirection: 'column' }}>
        <div style={{ minHeight: 48, flex: 'none', display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px 8px 16px', borderBottom: `1px solid ${C.divider}`, flexWrap: 'wrap' }}>
          <Icon name="calendar" size={15} color={C.purpleLt} />
          <span style={{ font: f(600, 14), color: C.text }}>{t('su.schedule')}</span>
          <span style={{ flex: 1 }} />
          <span style={{ font: f(400, 11.5), color: C.muted }} title={tc('setup.schedule.timezoneHint')}>{t('su.timezone')}</span>
          <Dropdown value={tz} options={tzOpts} onChange={onTz} width={170} menuWidth={220} />
          <button type="button" onClick={onClose} title={t('close')} className="cpt-hover-row" style={{ width: 28, height: 28, border: 0, borderRadius: 6, background: 'transparent', color: C.sub, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Icon name="x" size={15} />
          </button>
        </div>

        <div className="cpt-thin-scroll" style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: '14px 16px 16px', display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
              <Caps>{t('su.week')}</Caps>
              <span style={{ font: f(500, 11.5), color: C.sub }}>{flat.length ? t('su.hoursWeek', { count: hrs }) : t('su.noHours')}</span>
            </div>
            {days.map((d) => (
              <div key={d} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ width: 28, flex: 'none', font: f(500, 11), color: C.sub }}>{t(`su.dn.${d}`)}</span>
                <div style={{ position: 'relative', flex: 1, height: 12, background: C.input, border: `1px solid ${C.borderLo}`, borderRadius: 4, overflow: 'hidden' }}>
                  {(segs[d] ?? []).map(([l, w, r], i) => <span key={i} style={{ position: 'absolute', top: 0, bottom: 0, left: `${l}%`, width: `${w}%`, background: r ? C.purpleLt : C.purple }} />)}
                </div>
              </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'space-between', paddingLeft: 38, font: f(400, 10), color: C.muted }}>
              <span>00</span><span>06</span><span>12</span><span>18</span><span>24</span>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
              <Caps>{t('su.windows')}</Caps>
              <span style={{ font: f(400, 11.5), color: C.muted }}>{t('su.windowsHint')}</span>
            </div>

            {ws.map((w) => {
              const open = !!adv[w.key];
              const ch = windowChanges(w);
              const custom = Array.isArray(w.ignore_lists);
              const wl = w.ignore_lists ?? [];
              const nI = w.interrupts ? Object.keys(w.interrupts).length : 0;
              const noDays = !w.days.some(Boolean);
              const endStep = regimeCompletionStep(w.regime ?? regimeId);
              const chatterOn = w.call_chatter ?? chatter;
              const setInt = (name: string, v: string) => {
                const cur = { ...(w.interrupts ?? {}) };
                if (v === 'd') delete cur[name]; else cur[name] = v === 'on';
                patch(w.key, { interrupts: Object.keys(cur).length ? cur : null });
              };
              return (
                <div key={w.key} style={{ background: 'var(--om-panel)', border: `1px solid ${C.border}`, borderRadius: 8, display: 'flex', flexDirection: 'column' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 10px 6px 12px', flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', gap: 3, flex: 'none' }}>
                      {days.map((d) => {
                        const on = w.days[d];
                        return (
                          <button key={d} type="button" title={tc(`setup.schedule.days.${d}`)} onClick={() => patch(w.key, { days: w.days.map((x, j) => (j === d ? !x : x)) })} style={{ width: 24, height: 24, padding: 0, border: `1px solid ${on ? C.purple : C.borderHi}`, borderRadius: 5, background: on ? C.purple : C.input, color: on ? '#fff' : C.muted, font: f(600, 11), cursor: 'pointer' }}>
                            {t(`su.dl.${d}`)}
                          </button>
                        );
                      })}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 'none' }}>
                      <TimeInput value={w.start} onChange={(v) => { if (v !== w.end) patch(w.key, { start: v }); }} />
                      <span style={{ color: C.muted }}>–</span>
                      <TimeInput value={w.end} onChange={(v) => { const e = v === 0 ? 1440 : v; if (e !== w.start) patch(w.key, { end: e }); }} />
                    </div>
                    <span style={{ flex: 1, minWidth: 60, font: f(400, 11.5), color: noDays ? C.purplePale : C.muted, whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 5 }}>
                      {noDays ? <><Icon name="alert-circle" size={12} />{t('su.noDays')}</> : w.end < w.start ? t('su.nextDay') : ''}
                    </span>
                    <Dropdown value={w.regime ?? DEF} options={regOpts} onChange={(v) => patch(w.key, { regime: v === DEF ? null : v })} width={210} menuWidth={320} />
                    <button type="button" title={t('su.removeWindow')} onClick={() => commit(ws.filter((x) => x.key !== w.key))} className="cpt-hover-row" style={{ width: 28, height: 28, flex: 'none', border: 0, borderRadius: 6, background: 'transparent', color: C.muted, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Icon name="trash-2" size={13} />
                    </button>
                  </div>
                  {endStep !== null && (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '2px 10px 6px 12px', flexWrap: 'wrap' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
                        <span style={{ font: f(500, 12.5), color: w.call_chatter == null ? 'var(--om-body)' : C.text }}>{t('su.callChatter')}</span>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 5, font: f(400, 11.5), color: chatterOn ? C.muted : C.purplePale }}>
                          {!chatterOn && <Icon name="alert-circle" size={12} />}
                          {chatterOn ? t('su.chatterOn', { step: endStep }) : t('su.chatterOff', { step: endStep })}
                        </span>
                      </div>
                      <Seg
                        options={[{ id: 'd', label: t('su.defaultIs', { v: chatter ? t('su.on') : t('su.off') }) }, { id: 'on', label: t('su.on') }, { id: 'off', label: t('su.off') }]}
                        value={w.call_chatter == null ? 'd' : w.call_chatter ? 'on' : 'off'}
                        onChange={(v) => patch(w.key, { call_chatter: v === 'd' ? null : v === 'on' })}
                      />
                    </div>
                  )}
                  <div style={{ padding: '0 6px 6px' }}>
                    <AdvancedToggle open={open} onToggle={() => setAdv((a) => ({ ...a, [w.key]: !open }))} hint={ch ? t('su.nChanges', { count: ch }) : t('su.winAdvHint')} />
                  </div>
                  {open && (
                    <div style={{ display: 'flex', flexDirection: 'column', borderTop: `1px solid ${C.divider}` }}>
                      <Line label={t('su.ignoreLists')} help={custom ? t('su.winIgnoreCustom') : t('su.winIgnoreDefault')}>
                        <Seg options={[{ id: 'd', label: t('su.default') }, { id: 'c', label: t('su.custom') }]} value={custom ? 'c' : 'd'} onChange={(v) => patch(w.key, { ignore_lists: v === 'd' ? null : w.ignore_lists ?? [] })} />
                      </Line>
                      {custom && (
                        <ChipRow>
                          {wl.map((n) => <Chip key={n} label={n} onRemove={() => patch(w.key, { ignore_lists: wl.filter((x) => x !== n) })} />)}
                          {!wl.length && <span style={{ font: f(400, 12), color: C.muted }}>{t('su.winIgnoreNone')}</span>}
                          <AddLists pool={lists.filter((n) => !wl.includes(n)).map((n) => ({ id: n, label: n }))} onAdd={(a) => patch(w.key, { ignore_lists: [...wl, ...a] })} />
                        </ChipRow>
                      )}
                      <div style={{ borderTop: `1px solid ${C.divider}` }}>
                        <Line label={t('su.interrupts')} help={t('su.winIntHelp')}>
                          <span style={{ font: f(500, 12), color: C.sub, whiteSpace: 'nowrap' }}>{nI ? t('su.nChanged', { count: nI }) : t('su.allDefault')}</span>
                          {nI > 0 && <Btn kind="text" height={26} onClick={() => patch(w.key, { interrupts: null })}>{t('su.reset')}</Btn>}
                        </Line>
                        <Grid>
                          {FOLDER_INTERRUPT_OVERRIDE_FIELDS.map((fd) => {
                            const v = w.interrupts && fd.interruptName in w.interrupts ? (w.interrupts[fd.interruptName] ? 'on' : 'off') : 'd';
                            return (
                              <div key={fd.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '4px 12px', minHeight: 34, flexWrap: 'wrap', padding: '2px 0', borderBottom: '1px solid var(--om-card-2)' }}>
                                <span title={fd.label} style={{ font: f(400, 12.5), color: v === 'd' ? 'var(--om-body)' : C.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>{fd.label}</span>
                                <Seg options={tri} value={v} onChange={(x) => setInt(fd.interruptName, x)} />
                              </div>
                            );
                          })}
                        </Grid>
                      </div>
                      <div style={{ borderTop: `1px solid ${C.divider}` }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, padding: '10px 14px 4px' }}>
                          <span style={{ font: f(500, 13), color: C.text }}>{t('su.sales')}</span>
                          <span style={{ font: f(400, 11.5), color: C.sub }}>{t('su.winSalesHelp')}</span>
                        </div>
                        <Grid>
                          <SalesRow label={tc('setup.schedule.labelPpv')} opts={tri} value={w.ppv_active == null ? 'd' : w.ppv_active ? 'on' : 'off'} onChange={(v) => patch(w.key, { ppv_active: v === 'd' ? null : v === 'on' })} />
                          <SalesRow label={tc('setup.schedule.labelContent')} opts={modes(false)} value={w.content_mode ?? 'd'} onChange={(v) => patch(w.key, { content_mode: v === 'd' ? null : (v as 'handoff' | 'automate') })} />
                          <SalesRow label={tc('setup.schedule.labelCustom')} opts={modes(true)} value={w.custom_mode ?? 'd'} onChange={(v) => patch(w.key, { custom_mode: v === 'd' ? null : (v as 'handoff' | 'automate' | 'decline') })} />
                          <SalesRow label={tc('setup.schedule.labelCall')} opts={modes(true)} value={w.call_mode ?? 'd'} onChange={(v) => patch(w.key, { call_mode: v === 'd' ? null : (v as 'handoff' | 'automate' | 'decline') })} />
                        </Grid>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

            {!flat.length && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 12, background: 'var(--om-panel)', border: `1px dashed ${C.borderHi}`, borderRadius: 8, font: f(400, 12), color: C.purplePale }}>
                <Icon name="alert-circle" size={13} />{t('su.noWindows')}
              </div>
            )}
            <Btn kind="dashed" icon="plus" onClick={() => commit([...ws, newWindow()])} style={{ alignSelf: 'flex-start', color: C.purpleLt }}>{t('su.addWindow')}</Btn>
          </div>
        </div>

        <div style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px', borderTop: `1px solid ${C.divider}` }}>
          <span style={{ flex: 1, font: f(400, 11.5), color: C.muted }}>{t('save.auto')}</span>
          <Btn kind="primary" onClick={onClose}>{t('su.done')}</Btn>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function Caps({ children }: { children: string }) {
  return <span style={{ font: f(600, 10), letterSpacing: '.06em', textTransform: 'uppercase', color: C.muted }}>{children}</span>;
}

function Line({ label, help, children }: { label: string; help: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, padding: '10px 14px', flexWrap: 'wrap' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0, flex: '1 1 240px' }}>
        <span style={{ font: f(500, 13), color: C.text }}>{label}</span>
        <span style={{ font: f(400, 11.5), color: C.sub }}>{help}</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 'none' }}>{children}</div>
    </div>
  );
}

function Grid({ children }: { children: React.ReactNode }) {
  return <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(min(360px,100%),1fr))', gap: '0 24px', padding: '0 14px 10px' }}>{children}</div>;
}

function SalesRow({ label, opts, value, onChange }: { label: string; opts: { id: string; label: string }[]; value: string; onChange: (v: string) => void }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '4px 12px', minHeight: 36, flexWrap: 'wrap', padding: '2px 0' }}>
      <span style={{ font: f(400, 12.5), color: value === 'd' ? 'var(--om-body)' : C.text }}>{label}</span>
      <Seg options={opts} value={value} onChange={onChange} />
    </div>
  );
}

/** Native time input (30-minute steps) over minutes-from-midnight. */
function TimeInput({ value, onChange }: { value: number; onChange: (m: number) => void }) {
  return (
    <input
      type="time"
      step={1800}
      value={hhmm(value)}
      onChange={(e) => { const v = parseHhmm(e.target.value); if (v !== null) onChange(v); }}
      style={{ height: 28, boxSizing: 'border-box', padding: '0 6px', background: C.input, border: `1px solid ${C.borderHi}`, borderRadius: 6, color: C.text, font: f(500, 12.5), colorScheme: 'dark' }}
    />
  );
}
